import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { hasAdvertisingConsent } from "@/lib/consent";

/**
 * IndexPointer is imported dynamically here (consent.ts already does the
 * same) so the module lives in exactly one lazy chunk. A static import here
 * duplicated it into the entry bundle, which Lighthouse reports as
 * "duplicated JavaScript" on first load.
 */
const indexPointer = () => import("@/lib/indexpointer");

/**
 * Mounts inside <BrowserRouter>.
 * 1. Ensures the IndexPointer script is injected once on initial mount
 *    (idempotent, safe across route changes).
 * 2. On SPA route changes, forwards a lightweight page-view signal to
 *    IndexPointer if its global API is available.
 * 3. Respects consent — only activates when analytics consent is granted.
 */
export function IndexPointerTracker() {
  const location = useLocation();
  const isFirst = useRef(true);
  const consentGranted = hasAdvertisingConsent();

  useEffect(() => {
    if (!consentGranted) return;
    const first = isFirst.current;
    isFirst.current = false;
    const path = location.pathname + location.search;

    void indexPointer().then((m) => {
      // Only inject / track on production when consent is granted.
      if (!m.isProductionHost() || !hasAdvertisingConsent()) return;

      // Initial mount: inject script (idempotent).
      m.loadIndexPointer();

      // Skip the first render because the script itself will capture
      // the initial page load. We only forward SPA navigations.
      if (first) return;

      // Subsequent route changes: notify IndexPointer of SPA navigation.
      m.trackIndexPointerPageView(path);
    });
  }, [location.pathname, location.search, consentGranted]);

  return null;
}
